/**
 * On-Device AI Service
 * Uses Transformers.js (WebAssembly, runs in browser)
 * Zero cost, privacy-first
 */

import { pipeline, env } from '@xenova/transformers';

// Configure transformers to use browser cache
env.allowLocalModels = false;
env.useBrowserCache = true;

let textGenerator = null;
let sentiment = null;
let isInitialized = false;

/**
 * Initialize AI models (lazy loading)
 * Models are cached in browser after first load
 */
export async function initializeAI() {
  if (isInitialized) return;

  try {
    console.log('📥 Loading on-device AI models...');
    
    // Load small, efficient models
    // These run entirely in the browser (no API calls)
    textGenerator = await pipeline(
      'text-generation',
      'Xenova/distilgpt2', // 82MB, fast, good for insights
      { quantized: true } // Smaller model size
    );

    sentiment = await pipeline(
      'sentiment-analysis',
      'Xenova/distilbert-base-uncased-finetuned-sst-2-english' // 67MB
    );

    isInitialized = true;
    console.log('✅ On-device AI ready');
  } catch (error) {
    console.error('❌ AI initialization failed:', error);
    throw error;
  }
}

/**
 * Generate profit insights using on-device AI
 * @param {Object} stats - Shop profit statistics
 * @returns {Promise<string>} - AI-generated insight
 */
export async function generateProfitInsight(stats) {
  const margin = parseFloat(stats.net_margin ?? stats.avg_margin) || 0;
  const negativeCount = parseInt(stats.negative_margin_count) || 0;
  const revenue = parseFloat(stats.total_revenue) || 0;
  const profit = parseFloat(stats.total_profit) || 0;
  const orders = parseInt(stats.total_orders) || 0;
  const shipping = parseFloat(stats.total_shipping) || 0;
  const fees = parseFloat(stats.total_fees) || 0;
  const discounts = parseFloat(stats.total_discounts) || 0;
  const lowMarginProducts = parseInt(stats.low_margin_product_count) || 0;

  // Build contextual insight from actual metrics
  let insight = '';

  if (orders === 0) {
    return "No orders in the last 90 days yet. Once orders sync in, Profit Pulse AI will start surfacing profit leaks and margin trends.";
  }

  const leakDrivers = [
    { label: 'discounts', value: discounts },
    { label: 'shipping', value: shipping },
    { label: 'fees', value: fees }
  ].sort((a, b) => b.value - a.value);

  const topLeakDriver = leakDrivers[0]?.value > 0 ? leakDrivers[0].label : null;

  if (margin < 0) {
    insight = `Your true net margin is ${margin.toFixed(1)}% across ${orders} orders, so the business is leaking profit overall. Review negative-margin orders and low-margin products immediately.`;
  } else if (margin < 15) {
    insight = `Your net margin is only ${margin.toFixed(1)}%. ${negativeCount > 0 ? `${negativeCount} order${negativeCount > 1 ? 's are' : ' is'} already negative.` : ''} Protect margin by tightening discounts and repricing thin products.`;
  } else if (margin < 30) {
    insight = `Your net margin is ${margin.toFixed(1)}% across ${orders} orders. ${lowMarginProducts > 0 ? `${lowMarginProducts} product${lowMarginProducts > 1 ? 's are' : ' is'} below your margin threshold.` : 'Most products are holding up well.'}`;
  } else if (margin < 50) {
    insight = `Strong ${margin.toFixed(1)}% net margin generating $${profit.toFixed(2)} from $${revenue.toFixed(2)} revenue. ${negativeCount > 0 ? `${negativeCount} order${negativeCount > 1 ? 's still need' : ' still needs'} attention.` : 'Keep protecting your best performers.'}`;
  } else {
    insight = `Exceptional ${margin.toFixed(1)}% net margin and $${profit.toFixed(2)} profit from ${orders} orders. Your pricing and cost discipline are protecting margin well.`;
  }

  if (topLeakDriver) {
    insight += ` Your biggest non-COGS drag right now appears to be ${topLeakDriver}.`;
  }

  // Add sentiment label from AI if available (DistilBERT is good at this)
  try {
    if (isInitialized && sentiment) {
      const sentimentResult = await sentiment(`Business profit margin is ${margin}%`);
      const label = sentimentResult[0]?.label;
      if (label === 'NEGATIVE' && margin > 15) {
        // AI agrees margins could be better — no action needed, insight is already contextual
      }
    }
  } catch {
    // Sentiment analysis is optional
  }

  return insight;
}

/**
 * Analyze margin sentiment
 * @param {number} margin - Profit margin percentage
 * @returns {Promise<Object>} - Sentiment analysis result
 */
export async function analyzeMarginSentiment(margin) {
  if (!isInitialized) {
    await initializeAI();
  }

  try {
    const text = `A business has ${margin}% profit margin`;
    const result = await sentiment(text);
    return result[0]; // { label: 'POSITIVE'/'NEGATIVE', score: 0.0-1.0 }
  } catch (error) {
    console.error('Sentiment analysis error:', error);
    return { label: 'NEUTRAL', score: 0.5 };
  }
}

/**
 * Extract cost from text (using pattern matching + AI)
 * @param {string} text - Invoice or email text
 * @returns {Promise<number|null>} - Extracted cost or null
 */
export async function extractCostFromText(text) {
  // First try regex patterns (fast, no AI needed)
  const patterns = [
    /\$\s*(\d+\.?\d{0,2})/g, // $10.50
    /(\d+\.?\d{0,2})\s*USD/gi, // 10.50 USD
    /total[:\s]+\$?(\d+\.?\d{0,2})/gi, // Total: $10.50
    /cost[:\s]+\$?(\d+\.?\d{0,2})/gi // Cost: 10.50
  ];

  for (const pattern of patterns) {
    const matches = text.match(pattern);
    if (matches && matches.length > 0) {
      // Extract first number
      const numbers = matches.map(m => parseFloat(m.replace(/[^0-9.]/g, '')));
      if (numbers.length > 0) {
        return numbers[0];
      }
    }
  }

  // If regex fails, try AI extraction (slower but more accurate)
  try {
    if (!isInitialized) {
      await initializeAI();
    }

    const prompt = `Extract the product cost from this text. Reply with only the number:\n${text.substring(0, 500)}\n\nCost: $`;
    
    const result = await textGenerator(prompt, {
      max_new_tokens: 10,
      temperature: 0.3
    });

    const extracted = result[0].generated_text.replace(prompt, '').trim();
    const cost = parseFloat(extracted.match(/[\d.]+/)?.[0]);
    
    if (!isNaN(cost) && cost > 0 && cost < 100000) {
      return cost;
    }
  } catch (error) {
    console.error('AI cost extraction error:', error);
  }

  return null;
}


/**
 * Optional: Use user's own API key (BYOK)
 * Stored in localStorage, never sent to server
 */
export function setUserAPIKey(provider, apiKey) {
  if (!apiKey) {
    localStorage.removeItem(`profitpulse_${provider}_key`);
    return;
  }
  
  localStorage.setItem(`profitpulse_${provider}_key`, apiKey);
}

export function getUserAPIKey(provider) {
  return localStorage.getItem(`profitpulse_${provider}_key`);
}

/**
 * Call external AI API (if user provided their own key)
 * This is optional - app works fine with on-device AI only
 */
export async function callExternalAI(provider, prompt) {
  const apiKey = getUserAPIKey(provider);
  
  if (!apiKey) {
    throw new Error('No API key configured');
  }

  // Only call if user explicitly provided their key
  switch (provider) {
    case 'openai':
      return await callOpenAI(prompt, apiKey);
    case 'anthropic':
      return await callAnthropic(prompt, apiKey);
    default:
      throw new Error('Unsupported provider');
  }
}

async function callOpenAI(prompt, apiKey) {
  const response = await fetch('https://api.openai.com/v1/chat/completions', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${apiKey}`
    },
    body: JSON.stringify({
      model: 'gpt-4o-mini', // Cheapest model
      messages: [{ role: 'user', content: prompt }],
      max_tokens: 100
    })
  });

  const data = await response.json();
  return data.choices[0].message.content;
}

async function callAnthropic(prompt, apiKey) {
  const response = await fetch('https://api.anthropic.com/v1/messages', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': apiKey,
      'anthropic-version': '2023-06-01'
    },
    body: JSON.stringify({
      model: 'claude-3-haiku-20240307', // Cheapest model
      max_tokens: 100,
      messages: [{ role: 'user', content: prompt }]
    })
  });

  const data = await response.json();
  return data.content[0].text;
}

export default {
  initializeAI,
  generateProfitInsight,
  analyzeMarginSentiment,
  extractCostFromText,
  setUserAPIKey,
  getUserAPIKey,
  callExternalAI
};
